import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Columns3, Plus, Tag, Timer, Users, type LucideIcon } from 'lucide-react';
import { memberDisplayName, type LabelDto, type MemberDto, type WorkItemFull } from '../../lib/work-types';
import { apiFetch, v1Path } from '../../lib/api';
import { parentSubtaskBoardPath, workItemHasSubtasks } from '../../lib/work-item-path';
import type { ResourceLoad } from '../../lib/resource-types';
import LoadBar from './LoadBar';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  formatDateRange,
  labelChipStyle,
  MemberAvatar,
  selectedLabels,
  selectedMembers,
} from './WorkCardMeta';
import { useOverlays } from './overlay-context';

type Props = {
  item: WorkItemFull;
  labels: LabelDto[];
  members: MemberDto[];
  readOnly: boolean;
  /** Done or cancelled work keeps its dates but never reads as overdue. */
  finished?: boolean;
  onPatch: (fields: Record<string, unknown>) => Promise<unknown>;
};

/** State handed to a popover's trigger so it can render either shape. */
type TriggerState = {
  open: boolean;
  toggle: () => void;
  disabled: boolean;
};

function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((row) => row !== id) : [...ids, id];
}

function ActionPopover({
  disabled,
  trigger,
  children,
}: {
  disabled: boolean;
  trigger: (state: TriggerState) => ReactNode;
  children: ReactNode;
}) {
  const { pushBlock, popBlock } = useOverlays();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    pushBlock();
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      popBlock();
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, pushBlock, popBlock]);

  return (
    <div ref={rootRef} className="relative">
      {trigger({
        open,
        disabled,
        toggle: () => {
          if (disabled) return;
          setOpen((value) => !value);
        },
      })}
      {open ? (
        <div
          role="dialog"
          className="absolute left-0 top-full z-50 mt-1 w-72 rounded-md border bg-popover p-3 text-popover-foreground shadow-md"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** Trigger shown when the property has no value yet. */
function AddPropertyButton({
  icon: Icon,
  label,
  shortcut,
  open,
  toggle,
  disabled,
}: TriggerState & { icon: LucideIcon; label: string; shortcut: string }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={disabled}
      aria-expanded={open}
      aria-haspopup="dialog"
      data-shortcut={shortcut}
      className="h-8 gap-1.5 font-normal"
      onClick={toggle}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {label}
    </Button>
  );
}

/**
 * Trigger shown once the property has a value: the value itself under a small
 * heading, with a plus button that opens the same editor.
 */
function PropertySection({
  heading,
  shortcut,
  open,
  toggle,
  disabled,
  children,
}: TriggerState & { heading: string; shortcut: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{heading}</p>
      <div className="flex items-center gap-1">
        {children}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={`Edit ${heading.toLowerCase()}`}
          data-shortcut={shortcut}
          className="h-8 w-8 shrink-0"
          onClick={toggle}
        >
          <Plus className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

function SearchList({
  query,
  onQuery,
  placeholder,
  children,
}: {
  query: string;
  onQuery: (value: string) => void;
  placeholder: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Input
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        placeholder={placeholder}
        className="h-8"
      />
      <div className="max-h-56 space-y-1 overflow-y-auto">{children}</div>
    </div>
  );
}

export default function WorkItemPropertyActions({
  item,
  labels,
  members,
  readOnly,
  finished = false,
  onPatch,
}: Props) {
  const [labelQuery, setLabelQuery] = useState('');
  const [memberQuery, setMemberQuery] = useState('');
  const [loads, setLoads] = useState<Map<string, ResourceLoad>>(new Map());
  const hours = item.estimate_hours ?? null;

  useEffect(() => {
    let cancelled = false;
    void apiFetch<{ items: ResourceLoad[] }>(v1Path('/resources/loads'))
      .then((res) => {
        if (cancelled) return;
        setLoads(new Map(res.items.map((row) => [row.user_id, row])));
      })
      .catch(() => {
        if (!cancelled) setLoads(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleLabels = useMemo(() => {
    const q = labelQuery.trim().toLowerCase();
    if (!q) return labels;
    return labels.filter((label) => label.name.toLowerCase().includes(q));
  }, [labels, labelQuery]);

  const visibleMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return members;
    return members.filter((member) => (member.email ?? member.user_id).toLowerCase().includes(q));
  }, [members, memberQuery]);

  const itemLabels = selectedLabels(item.label_ids, labels);
  const itemMembers = selectedMembers(item.assignee_ids, members);
  const dates = formatDateRange(item.start_date, item.due_date);


  return (
    <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
      <ActionPopover
        disabled={readOnly}
        trigger={(state) =>
          itemLabels.length > 0 ? (
            <PropertySection heading="Labels" shortcut="labels" {...state}>
              <div className="flex flex-wrap items-center gap-1">
                {itemLabels.map((label) => (
                  <span
                    key={label.id}
                    title={label.name}
                    style={labelChipStyle(label.color)}
                    className="inline-flex h-8 max-w-[10rem] items-center truncate rounded-md bg-muted px-2.5 text-xs font-medium"
                  >
                    {label.name}
                  </span>
                ))}
              </div>
            </PropertySection>
          ) : (
            <AddPropertyButton icon={Tag} label="Labels" shortcut="labels" {...state} />
          )
        }
      >
        <p className="mb-2 text-sm font-medium">Labels</p>
        <SearchList query={labelQuery} onQuery={setLabelQuery} placeholder="Search labels">
          {visibleLabels.length === 0 ? (
            <p className="px-1 py-2 text-xs text-muted-foreground">No labels</p>
          ) : (
            visibleLabels.map((label) => {
              const checked = item.label_ids.includes(label.id);
              return (
                <label
                  key={label.id}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1.5 text-sm hover:bg-accent"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => {
                      if (!label.id) return;
                      void onPatch({ label_ids: toggleId(item.label_ids, label.id) }).catch(
                        () => undefined,
                      );
                    }}
                  />
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: label.color }}
                    aria-hidden
                  />
                  <span className="min-w-0 truncate">{label.name}</span>
                </label>
              );
            })
          )}
        </SearchList>
      </ActionPopover>

      <ActionPopover
        disabled={readOnly}
        trigger={(state) =>
          dates ? (
            <PropertySection heading="Due" shortcut="dates" {...state}>
              <span
                className={cn(
                  'inline-flex h-8 items-center gap-1.5 rounded-md bg-muted px-2.5 text-xs font-medium',
                  dates.overdue &&
                    !finished &&
                    'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
                )}
              >
                <Clock className="h-3.5 w-3.5" aria-hidden />
                {dates.text}
              </span>
            </PropertySection>
          ) : (
            <AddPropertyButton icon={Calendar} label="Due date" shortcut="dates" {...state} />
          )
        }
      >
        <p className="mb-3 text-sm font-medium">Dates</p>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="work-item-start-date" className="text-xs text-muted-foreground">
              Start
            </Label>
            <Input
              id="work-item-start-date"
              type="date"
              className="h-8"
              value={item.start_date ?? ''}
              disabled={readOnly}
              onChange={(event) => {
                const value = event.target.value;
                void onPatch({ start_date: value === '' ? null : value }).catch(() => undefined);
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="work-item-due-date" className="text-xs text-muted-foreground">
              Due
            </Label>
            <Input
              id="work-item-due-date"
              type="date"
              className="h-8"
              value={item.due_date ?? ''}
              disabled={readOnly}
              onChange={(event) => {
                const value = event.target.value;
                void onPatch({ due_date: value === '' ? null : value }).catch(() => undefined);
              }}
            />
          </div>
        </div>
      </ActionPopover>

      <ActionPopover
        disabled={readOnly}
        trigger={(state) =>
          hours != null ? (
            <PropertySection heading="Estimate" shortcut="estimate" {...state}>
              <span className="inline-flex h-8 items-center rounded-md bg-muted px-2.5 text-xs font-medium tabular-nums">
                {hours}h
              </span>
            </PropertySection>
          ) : (
            <AddPropertyButton icon={Timer} label="Estimate" shortcut="estimate" {...state} />
          )
        }
      >
        <p className="mb-2 text-sm font-medium">Estimate hours</p>
        <Input
          type="number"
          min={0}
          max={1000}
          step={0.5}
          className="h-8"
          defaultValue={hours ?? ''}
          disabled={readOnly}
          onBlur={(event) => {
            const raw = event.target.value.trim();
            const next = raw === '' ? null : Number(raw);
            if (raw !== '' && !Number.isFinite(next)) return;
            if (next === hours) return;
            void onPatch({ estimate_hours: next }).catch(() => undefined);
          }}
        />
      </ActionPopover>

      <ActionPopover
        disabled={readOnly}
        trigger={(state) =>
          itemMembers.length > 0 ? (
            <PropertySection heading="Owner" shortcut="assignees" {...state}>
              <div className="flex -space-x-1.5">
                {itemMembers.map((member) => (
                  <MemberAvatar
                    key={member.user_id}
                    member={member}
                    size="md"
                    className="border-2 border-background"
                  />
                ))}
              </div>
            </PropertySection>
          ) : (
            <AddPropertyButton icon={Users} label="Owner" shortcut="assignees" {...state} />
          )
        }
      >
        <p className="mb-2 text-sm font-medium">Assign member</p>
        <SearchList query={memberQuery} onQuery={setMemberQuery} placeholder="Search members">
          {visibleMembers.length === 0 ? (
            <p className="px-1 py-2 text-xs text-muted-foreground">No members</p>
          ) : (
            visibleMembers.map((member) => {
              const checked = item.assignee_ids.includes(member.user_id);
              const load = loads.get(member.user_id);
              return (
                <label
                  key={member.user_id}
                  className="flex cursor-pointer items-start gap-2 rounded-md px-1 py-1.5 text-sm hover:bg-accent"
                >
                  <Checkbox
                    className="mt-0.5"
                    checked={checked}
                    onCheckedChange={() => {
                      if (!member.user_id) return;
                      void onPatch({
                        assignee_ids: toggleId(item.assignee_ids, member.user_id),
                      }).catch(() => undefined);
                    }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{memberDisplayName(member)}</span>
                    {load ? (
                      <>
                        <LoadBar percent={load.load_percent} band={load.load_band} className="mt-1" />
                        <span className="mt-0.5 block text-[11px] tabular-nums text-muted-foreground">
                          {load.load_percent}%
                          {load.load_band === 'overloaded'
                            ? ` · over by ${Math.round((load.allocated_hours - load.weekly_capacity_hours) * 10) / 10}h`
                            : ` · ${load.available_hours}h remaining`}
                        </span>
                      </>
                    ) : null}
                  </span>
                </label>
              );
            })
          )}
        </SearchList>
      </ActionPopover>

      {workItemHasSubtasks(item) ? (
        <Button asChild variant="outline" size="sm" className="h-8 gap-1.5 font-normal">
          <Link to={parentSubtaskBoardPath(item.project_id, item.id)}>
            <Columns3 className="h-3.5 w-3.5" aria-hidden />
            Board
          </Link>
        </Button>
      ) : null}
    </div>
  );
}
